import moment from "moment";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import AppText from "../../components/Core/AppText/AppText";
import { CreateButton } from "../../components/Core/Button/Button";
import Flex from "../../components/Layout/Flex";
import TableLayout, { ActionsTableDataStyles } from "../../components/Layout/TableLayout";
import CustomerBlockModal from "../../components/Shared/CustomerBlockModal/CustomerBlockModal";
import EmptyState from "../../components/Shared/EmptyState/EmptyState";
import { DATE_TIME_FORMAT_TYPES } from "../../configs/constants/DateTimeFormatTypes";
import { ICON_TYPE } from "../../configs/constants/IconsTypes";
import Consultant from "../../models/Consultant";
import { BlockedCustomer } from "../../models/Customer";
import { useBlockedCustomers, useUnblockCustomer } from "../../networking/consultant.hooks";
import { ConfirmModalProps } from "../../stores/confirmModal.store";
import { useConfirmModalStore } from "../../stores/confirmModal.store";

const ConsultantCustomerBlockTab: React.FC<{ consultant: Consultant }> = ({ consultant }) => {
	const modalStore = useConfirmModalStore();

	const [modalOpen, setModalOpen] = useState(false);

	const { blockedCustomers } = useBlockedCustomers(consultant?.id);
	const { mutate: unblockCustomerMutation } = useUnblockCustomer();

	const { t } = useTranslation();

	const unblockCustomer = (block: BlockedCustomer) => {
		if (consultant?.id) {
			// The mutation invalidates the blocked-customers query on success.
			unblockCustomerMutation({ consultantId: consultant.id, customerId: block.customer.id });
		}
	};

	const unblockCustomerConfirm = (block: BlockedCustomer) => {
		const unblockCustomerConfirmModal: ConfirmModalProps = {
			title: "UNBLOCK_CUSTOMER_TITLE",
			text: "UNBLOCK_CUSTOMER_TEXT",
			acceptButtonText: "YES",
			rejectButtonText: "NO",
			onAccept: () => unblockCustomer(block),
			onReject: () => {},
			type: "SUCCESS",
		};
		modalStore.newModal(unblockCustomerConfirmModal);
	};

	return (
		<div>
			<Flex margin="1.5rem 0">
				<CreateButton onClick={() => setModalOpen(true)}>{t("ADD_BLOCK")}</CreateButton>
			</Flex>

			{blockedCustomers.length > 0 ? (
				<TableLayout selectable={false}>
					<table>
						<thead>
							<tr>
								<th style={{ minWidth: 200 }}> {t("CUSTOMER")} </th>
								<th style={{ minWidth: 300 }}> {t("customerBlock.comment")} </th>
								<th> {t("CREATED")} </th>
								<th> {t("CREATED_BY")} </th>
							</tr>
						</thead>
						<tbody>
							{blockedCustomers.map((block) => {
								return (
									<tr key={block.customer.id}>
										<td>{block?.customer?.name}</td>
										<td style={{ fontStyle: "italic" }}>
											<AppText size="s">{block.comment}</AppText>
										</td>
										<td>{moment(block?.metadata?.created).format(DATE_TIME_FORMAT_TYPES.DATE)}</td>
										<td>
											{block.metadata.createdBy?.firstName} {block.metadata.createdBy?.lastName}
										</td>
										<ActionsTableDataStyles onClick={() => unblockCustomerConfirm(block)}>
											<img src={ICON_TYPE.CROSS} alt="" />
										</ActionsTableDataStyles>
									</tr>
								);
							})}
						</tbody>
					</table>
				</TableLayout>
			) : (
				<EmptyState label="NO_BLOCKED_CUSTOMERS" />
			)}
			{consultant?.id && modalOpen && (
				<CustomerBlockModal
					blockedCustomers={blockedCustomers}
					consultantId={consultant.id}
					onClose={() => setModalOpen(false)}
				/>
			)}
		</div>
	);
};

export default ConsultantCustomerBlockTab;
